import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CHART_COLORS, chartColorsFor, activeChartPalette, LODGE_CHART_PALETTE } from '../../../styles/chartColors';
import { ValueLabels } from '../../charts/value-labels';
import { useChartStable } from '../../charts/chart-context';
import { BarLoadingSkeleton, LineLoadingSweep } from '../../charts/loading-sweep';
import { getFormatter } from '../formatters';
import { prefersReducedMotion } from '../AnimatedValue';

/**
 * Shared Recharts theming for the ReportsNative chart wrappers.
 * Recharts emits SVG presentation attributes, where CSS variables don't
 * resolve — so chart colors come from CHART_COLORS plus a dark-mode
 * observer on <html class="dark"> (same mechanism ThemeContext uses).
 * The palette follows html[data-chart-palette] (Settings → Appearance →
 * Charts, New layout only; see chartColors.js CHART_PALETTES).
 */

export function useIsDarkMode() {
  const [dark, setDark] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      setDark(root.classList.contains('dark'));
    });
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return dark;
}

/** Active chart palette id (html[data-chart-palette]); re-renders when it changes. */
export function useChartPaletteId() {
  const [id, setId] = useState(activeChartPalette);
  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => setId(activeChartPalette()));
    observer.observe(root, { attributes: true, attributeFilter: ['data-chart-palette', 'data-shell'] });
    return () => observer.disconnect();
  }, []);
  return id;
}

/** Reference-line label ink: brand orange on Lodge, muted grey on Ink, the lead colour otherwise. */
export function referenceLabelColor(dark, paletteId = activeChartPalette()) {
  if (paletteId === LODGE_CHART_PALETTE) return dark ? '#FFA34D' : '#E56E00'; // --accent-ink
  if (paletteId === 'ink') return dark ? '#A0A0AE' : '#52525F';
  return chartColorsFor(dark, paletteId).primary;
}

/** Target/budget band fill + edge: orange tint on Lodge, neutral otherwise. */
export function bandColors(dark, paletteId = activeChartPalette()) {
  if (paletteId === LODGE_CHART_PALETTE) return { fill: dark ? 'rgba(255,122,0,.10)' : '#FFF4ED', stroke: '#FFC999' };
  return { fill: dark ? 'rgba(255,255,255,.06)' : '#F4F4F7', stroke: dark ? '#52525F' : '#C8C8D4' };
}

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

/** Index-map old rows onto new rows and interpolate shared numeric fields. */
function interpolateRows(from, to, t) {
  const factor = from.length / to.length;
  return to.map((row, i) => {
    const prev = from[Math.floor(i * factor)];
    if (!prev || typeof row !== 'object' || row === null) return row;
    const out = { ...row };
    for (const key of Object.keys(row)) {
      const a = prev[key];
      const b = row[key];
      if (typeof a === 'number' && typeof b === 'number' && Number.isFinite(a) && Number.isFinite(b)) {
        out[key] = a + (b - a) * t;
      }
    }
    return out;
  });
}

/**
 * Tween chart data old→new whenever its VALUES change, returning the
 * in-flight frame to feed recharts. Recharts' own animations stay off
 * (`isAnimationActive={false}` everywhere): its entry animation is
 * broken in this app (the appear clipPath never expands, charts render
 * invisible) and its update tween can fall back to that same entry path.
 * Tweening the data ourselves gives the morph without that risk — every
 * frame is a normal static recharts render.
 *
 * Value changes are detected by JSON signature, so re-renders that
 * rebuild the same rows (new array identity) don't retrigger motion.
 */
export function useTweenedData(data, duration = 650) {
  const sig = useMemo(() => {
    try {
      return JSON.stringify(data);
    } catch (e) {
      return null;
    }
  }, [data]);
  const [display, setDisplay] = useState(data);
  const ref = useRef({ sig, data, raf: null, mounted: false });

  useEffect(() => {
    const st = ref.current;
    if (st.raf) cancelAnimationFrame(st.raf);
    if (!st.mounted || st.sig === null || sig === null || st.sig === sig) {
      st.mounted = true;
      st.sig = sig;
      st.data = data;
      setDisplay(data);
      return undefined;
    }
    const from = st.data;
    st.sig = sig;
    st.data = data;
    if (
      prefersReducedMotion() ||
      !Array.isArray(from) ||
      !Array.isArray(data) ||
      from.length === 0 ||
      data.length === 0
    ) {
      setDisplay(data);
      return undefined;
    }
    let start;
    const step = (ts) => {
      if (start === undefined) start = ts;
      const t = Math.min(1, (ts - start) / duration);
      setDisplay(t >= 1 ? data : interpolateRows(from, data, easeOutCubic(t)));
      if (t < 1) st.raf = requestAnimationFrame(step);
    };
    st.raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(st.raf);
  }, [sig, data, duration]);

  return display;
}

export function useChartTheme() {
  const dark = useIsDarkMode();
  const paletteId = useChartPaletteId();
  const colors = chartColorsFor(dark, paletteId);
  return {
    dark,
    paletteId,
    colors,
    band: bandColors(dark, paletteId),
    grid: colors.grid,
    axisLine: colors.grid,
    tick: colors.axis,
    label: dark ? '#D0D0DC' : '#3A3A44',
    pointFill: dark ? '#1A1A20' : '#ffffff', // --surface-1
    referenceLabel: referenceLabelColor(dark, paletteId),
    tooltip: {
      backgroundColor: dark ? '#22222A' : '#ffffff', // --surface-2
      border: `1px solid ${dark ? '#2A2A33' : '#E8E8F0'}`, // --border-subtle
      borderRadius: 8,
      color: dark ? '#F0F0F5' : '#0F0F11',
      boxShadow: dark ? '0 4px 12px rgba(0,0,0,.5)' : '0 4px 12px rgba(15,15,17,.10)',
      fontFamily: 'var(--font-sans)',
      fontSize: 12.5,
    },
  };
}

// Numeric face for chart labels/tooltips: the report typography scope in
// ReportsNative.css resolves it (JetBrains Mono in the classic shell, Geist
// in the inset shell). Must be applied via `style`, never as an SVG
// presentation attribute, which can't read CSS variables.
export const MONO = 'var(--font-mono)';

/** 12px mono tick style per kit spec. */
export function tickProps(theme, fontSize = 12) {
  return {
    tickLine: false,
    fontSize,
    stroke: theme.tick,
    style: { fontFamily: MONO, fontVariantNumeric: 'tabular-nums' },
  };
}

/** Y-axis tick formatter: compact variant of the value format. */
export function compactFormat(yFormat) {
  if (typeof yFormat === 'function') return yFormat;
  if (yFormat === 'currency' || yFormat === 'currency2' || yFormat === 'currencyCompact') return 'currencyCompact';
  if (yFormat === 'percent' || yFormat === 'percent1' || yFormat === 'percent2') return yFormat;
  return 'numberCompact';
}

/** Tooltip props shared by every chart wrapper (white card per mocks). */
export function tooltipProps(theme, yFormat, currency) {
  const fmt = getFormatter(yFormat, currency ? { currency } : undefined);
  return {
    contentStyle: theme.tooltip,
    itemStyle: { fontFamily: MONO, fontVariantNumeric: 'tabular-nums', fontSize: 12 },
    labelStyle: { color: theme.tick, fontWeight: 600, marginBottom: 4 },
    cursor: { stroke: theme.grid, strokeWidth: 1 },
    formatter: (value, name) => [fmt(value), name],
  };
}

/** Resolve series colors: explicit color → mode-appropriate ramp by index. */
const RAMP_KEYS = ['primary', 'secondary', 'tertiary'];
export function seriesColor(series, index, dark) {
  if (series.color) return series.color;
  const palette = chartColorsFor(dark);
  return palette[RAMP_KEYS[index % RAMP_KEYS.length]];
}

/* ------------------------------------------------------------------ */
/* Bklit kit helpers (see src/components/charts/LODGEIC.md)            */
/* ------------------------------------------------------------------ */

/** Synthetic x key added by toBklitRows — spacing only, never shown. */
export const SYNTH_X_KEY = '__rnX';

export function isNumeric(v) {
  return typeof v === 'number' && Number.isFinite(v);
}

function coerceNumeric(v) {
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return v;
}

/**
 * The kit's Line/Area render non-numeric values at the TOP of the plot
 * (getY falls back to 0) instead of leaving a gap, and every series shares
 * one data array — so interpolate interior gaps and clamp leading/trailing
 * gaps to the nearest real value. Series with no numeric values at all are
 * left untouched (callers skip rendering them via seriesHasData).
 *
 * openEnds: leave leading/trailing gaps empty instead. Only for Line series
 * (the Line draws a defined-aware path, so the series simply stops) — used
 * where a series legitimately ends early, e.g. a typical-pace benchmark that
 * only reaches 34 days out. Clamping drew a flat fake line to the edge.
 */
function fillKeyGaps(rows, key, openEnds = false) {
  const idxs = [];
  for (let i = 0; i < rows.length; i++) {
    if (isNumeric(rows[i][key])) idxs.push(i);
  }
  if (idxs.length === 0) return;
  const first = idxs[0];
  const last = idxs[idxs.length - 1];
  if (!openEnds) {
    for (let i = 0; i < first; i++) rows[i][key] = rows[first][key];
    for (let i = last + 1; i < rows.length; i++) rows[i][key] = rows[last][key];
  }
  for (let a = 0; a < idxs.length - 1; a++) {
    const i0 = idxs[a];
    const i1 = idxs[a + 1];
    if (i1 - i0 <= 1) continue;
    const v0 = rows[i0][key];
    const v1 = rows[i1][key];
    for (let i = i0 + 1; i < i1; i++) rows[i][key] = v0 + ((v1 - v0) * (i - i0)) / (i1 - i0);
  }
}

/**
 * Categorical report rows → bklit time-series rows: the kit's time-series
 * shells coerce x through `new Date()`, so category labels get evenly
 * spaced synthetic dates under SYNTH_X_KEY (pass display labels via the
 * shell's `xLabels` and a ChartTooltip `title`). Each row keeps the source
 * row under `__rnSrc` (tooltips read raw, un-gap-filled values from it)
 * and its index under `__rnIdx`. Rows where every series value is
 * non-numeric are dropped (the kit would draw them at the top of the plot).
 *
 * `fillKeys` restricts gap filling to a subset (line/area keys) — bar
 * series must keep nulls, which the kit's bar renderers skip natively.
 */
export function toBklitRows(data = [], seriesKeys = [], { fillKeys, openEnds = false } = {}) {
  const keys = seriesKeys.filter(Boolean);
  const rows = [];
  for (let i = 0; i < data.length; i++) {
    const src = data[i];
    if (!src || typeof src !== 'object') continue;
    const row = { ...src, __rnSrc: src };
    for (const k of keys) row[k] = coerceNumeric(src[k]);
    if (keys.length > 0 && !keys.some((k) => isNumeric(row[k]))) continue;
    rows.push(row);
  }
  rows.forEach((row, i) => {
    row.__rnIdx = i;
    row[SYNTH_X_KEY] = new Date(Date.UTC(2024, 0, 1 + i));
  });
  for (const k of fillKeys ? fillKeys.filter(Boolean) : keys) fillKeyGaps(rows, k, openEnds);
  return rows;
}

/**
 * Skeleton chrome for a chart that has no payload yet.
 *
 * Drawn as a LAYER: the kit's sweep primitives size themselves from the plot
 * box and never touch the chart's data or scales. That matters — feeding
 * placeholder VALUES through a mounted chart instead poisons its y-domain
 * (the domain follows the data, and a chart that never re-enters a live
 * phase keeps the placeholder scale: an occupancy axis stuck at 200%), and
 * driving the kit's `status="loading"` choreography needs a series whose
 * loading pulse reports completion — which bar-only charts don't have, so
 * they hang in the exit phase forever.
 *
 * Keeping the chart mounted and swapping only this layer is what makes the
 * payload arrive as the series drawing itself in, rather than one component
 * tree replacing another (which reads as a page reload).
 *
 * Series are drawn in their OWN color, muted, so the skeleton reads as the
 * real chart not yet resolved.
 */
export const SKELETON_STROKE_OPACITY = 0.38;
export const SKELETON_FILL_OPACITY = 0.3;

export function ChartSkeletonLayer({ kind = 'line', color, curve, withArea = false, barCount = 12 }) {
  if (kind === 'bar') {
    return <BarSkeletonLayer barCount={barCount} fill={color} />;
  }
  return (
    <LineLoadingSweep
      curve={curve}
      mode="loop"
      stroke={color}
      strokeOpacity={SKELETON_STROKE_OPACITY}
      strokeWidth={2}
      withArea={withArea}
    />
  );
}
ChartSkeletonLayer.displayName = 'ChartSkeletonLayer';

/**
 * The kit's swept placeholder bars, wired to the enclosing chart's plot box.
 * `BarChart` drops these in itself while loading; `ComposedChart` doesn't,
 * so column wrappers render this layer in place of their bar series.
 */
export function BarSkeletonLayer({ barCount = 12, fill, fillOpacity = SKELETON_FILL_OPACITY }) {
  const { innerWidth, innerHeight } = useChartStable();
  return (
    <BarLoadingSkeleton
      barCount={barCount}
      fill={fill}
      fillOpacity={fillOpacity}
      innerHeight={innerHeight}
      innerWidth={innerWidth}
    />
  );
}
BarSkeletonLayer.displayName = 'BarSkeletonLayer';

/** True when at least one row has a numeric value for `key`. */
export function seriesHasData(rows, key) {
  return rows.some((r) => isNumeric(r[key]));
}

/**
 * Kit marker config matching the recharts white-filled dot: outer radius
 * `r` with a 2px colored ring around a surface-colored center.
 */
export function pointMarkers(color, r = 2.6) {
  return {
    radius: Math.max(r - 1, 1.2),
    ringGap: 0,
    strokeWidth: 2,
    fill: 'var(--chart-background)',
    stroke: color,
    outlineWidth: 0,
  };
}

/** Point/value label ink (useChartTheme().label without the full theme). */
export function labelColor(dark) {
  return dark ? '#D0D0DC' : '#3A3A44';
}

/**
 * ValueLabels wrapper whose element carries NO `dataKey` prop — the kit
 * shells register any child with a string `dataKey` as a series (BarChart
 * would even shrink bar widths for the phantom series), so wrappers must
 * mount labels through this component. Renders the kit ValueLabels with the
 * mono label style.
 */
export function RnPointLabels({
  k,
  formatter,
  fill,
  fontSize = 9.5,
  fontWeight = 600,
  position = 'top',
  dy = 0,
  yAxisId,
}) {
  return (
    <ValueLabels
      dataKey={k}
      formatter={formatter}
      fill={fill}
      fontSize={fontSize}
      fontWeight={fontWeight}
      fontFamily={MONO}
      position={position}
      dy={dy}
      yAxisId={yAxisId}
    />
  );
}
RnPointLabels.displayName = 'RnPointLabels';

export { CHART_COLORS };
